/* ewebjelly-video-slider.js
 * - Vanilla JS slider for .ewebjelly-video-slider
 * - Supports: arrows, dots, swipe/drag
 * - Handles media on slide change: pauses all, plays active slide
 * - Autoplay slider setting: 0 = off, 1–10 = seconds  (data-autoplay-seconds)
 * - Video start delay setting: 0 = immediate, 1–10 = seconds (data-video-start-delay)
 * - Overlay timing + enforce mute
 *
 * PATCH:
 * - Do not initiate drag on interactive elements (links/buttons/etc)
 * - If user actually dragged, cancel click to avoid accidental navigation
 */

(() => {
  "use strict";

  const sliders = document.querySelectorAll(".ewebjelly-video-slider");
  if (!sliders.length) return;

  const prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const toBool = (val) => String(val) === "true";
  const toNum = (val, fallback = 0) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  };

  // ✅ NEW: identify link/button/etc targets so clicks work
  const isInteractiveTarget = (e) => {
    const t = e && e.target;
    if (!t || !t.closest) return false;

    // The whole-slide media link is deliberately NOT treated as interactive.
    // It covers the entire slide, so bailing out of onPointerDown here would
    // mean every touch landed on it and swipe stopped working altogether.
    // Accidental navigation is already handled: the capture-phase click
    // listener below cancels the click whenever movedDuringDrag is set, which
    // is exactly the case this exemption creates.
    if (t.closest(".ewebjelly-vs-media-link")) return false;

    return !!t.closest("a,button,input,textarea,select,label,[role='button']");
  };

  sliders.forEach((root) => {
    const viewport = root.querySelector(".ewebjelly-vs-viewport");
    const track = root.querySelector(".ewebjelly-vs-track");
    const slides = Array.from(root.querySelectorAll(".ewebjelly-vs-slide"));

    if (!viewport || !track || slides.length === 0) return;

    const showArrows = toBool(root.dataset.arrows);
    const showDots = toBool(root.dataset.dots);
    const showOverlay = toBool(root.dataset.overlay);
    const overlaySeconds = Math.max(1, toNum(root.dataset.overlaySeconds, 10));

    // Ceiling is 60, not the schema's max. Raising the range in the schema
    // should not also require editing this file — the old Math.min(10, …)
    // silently clamped anything above 10 back down, so a merchant setting 12
    // got 10 with no indication why.
    const autoplaySeconds = Math.max(
      0,
      Math.min(60, toNum(root.dataset.autoplaySeconds, 0))
    );

    // NEW: delay before video starts playing after slide becomes active
    const videoStartDelay = Math.max(
      0,
      Math.min(10, toNum(root.dataset.videoStartDelay, 0))
    );

    const enforceMute = toBool(root.dataset.enforceMute);

    const prevBtn = showArrows ? root.querySelector(".ewebjelly-vs-prev") : null;
    const nextBtn = showArrows ? root.querySelector(".ewebjelly-vs-next") : null;
    const dotsWrap = showDots ? root.querySelector(".ewebjelly-vs-dots") : null;

    let currentIndex = 0;
    let slideWidth = 0;

    // overlay timers
    let overlayTimer = null;

    // autoplay timers
    let autoplayTimer = null;

    // NEW: video start delay timer
    let videoStartTimer = null;

    let isHovering = false;
    let isFocused = false;
    let isDragging = false;

    // ✅ NEW: used to cancel click only if there was real movement
    let movedDuringDrag = false;

    // ---- Media control helpers ----

    // A slide can carry two cuts of the same video — .ewebjelly-vs-src--desktop
    // and --mobile — with CSS showing one. Only the visible one should ever be
    // loaded or played, or the byte savings that whole preload dance bought are
    // handed straight back.
    //
    // Detected with offsetParent rather than matchMedia on purpose: offsetParent
    // is null exactly when an ancestor is display:none, so the 750px breakpoint
    // stays defined once, in the stylesheet, and can't drift out of sync here.
    //
    // Single-source slides skip the check entirely, so nothing about the
    // existing behaviour changes for them.
    const slideVideos = (slide) => {
      if (!slide) return [];

      const all = Array.from(slide.querySelectorAll("video"));
      if (all.length < 2) return all;

      const visible = all.filter((v) => {
        const wrap = v.closest(".ewebjelly-vs-src");
        return !wrap || wrap.offsetParent !== null;
      });

      // If the whole slider is hidden every wrapper reports null. Fall back to
      // the first cut rather than silently playing nothing.
      return visible.length ? visible : all.slice(0, 1);
    };

    const enforceMuteOnSlide = (slide) => {
      if (!enforceMute || !slide) return;

      // MP4
      slideVideos(slide).forEach((v) => {
        try {
          v.muted = true;
          v.volume = 0;
          v.setAttribute("muted", "muted");
        } catch (_) {}
      });
    };

    const pauseSlideMedia = (slide) => {
      if (!slide) return;

      // MP4 — every cut, not just the visible one. After a resize across the
      // breakpoint the cut that just went display:none is still playing, and
      // a hidden <video> keeps decoding audio/video until told otherwise.
      slide.querySelectorAll("video").forEach((v) => {
        try {
          v.pause();
        } catch (_) {}
      });

      // YouTube/Vimeo
      const iframe = slide.querySelector("iframe.ewebjelly-vs-iframe");
      if (!iframe || !iframe.contentWindow) return;

      const provider = iframe.dataset.provider;

      try {
        if (provider === "youtube") {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
            "*"
          );
        } else if (provider === "vimeo") {
          iframe.contentWindow.postMessage({ method: "pause" }, "*");
        }
      } catch (_) {}
    };

    // Slides after the first are rendered with preload="none" so they cost
    // nothing on page load (see ewebjelly-video-slider.liquid). Promote one to
    // a real download only when we actually need it.
    const primeSlideVideo = (slide, { play = false } = {}) => {
      if (!slide) return;

      slideVideos(slide).forEach((v) => {
        try {
          if (v.preload === "none") {
            v.preload = play ? "auto" : "metadata";
            v.load();
          }

          if (play) {
            v.muted = true;
            v.volume = 0;
            v.setAttribute("muted", "muted");

            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          }
        } catch (_) {}
      });
    };

    // Once the visible slide is running, quietly fetch metadata for the next one
    // so advancing the carousel doesn't stall on a cold video.
    const prewarmNextSlide = (index) => {
      if (!slides || !slides.length) return;
      const nextIndex = (index + 1) % slides.length;
      if (nextIndex === index) return;
      primeSlideVideo(slides[nextIndex], { play: false });
    };

    const playSlideMedia = (slide) => {
      if (!slide) return;

      // MP4
      primeSlideVideo(slide, { play: true });

      // YouTube/Vimeo
      const iframe = slide.querySelector("iframe.ewebjelly-vs-iframe");
      if (!iframe || !iframe.contentWindow) return;

      const provider = iframe.dataset.provider;

      try {
        if (provider === "youtube") {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "command", func: "mute", args: [] }),
            "*"
          );
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "command", func: "playVideo", args: [] }),
            "*"
          );
        } else if (provider === "vimeo") {
          iframe.contentWindow.postMessage({ method: "setVolume", value: 0 }, "*");
          iframe.contentWindow.postMessage({ method: "play" }, "*");
        }
      } catch (_) {}
    };

    // NEW: delayed play control
    const clearVideoStartTimer = () => {
      if (videoStartTimer) {
        clearTimeout(videoStartTimer);
        videoStartTimer = null;
      }
    };

    const playActiveSlideWithDelay = () => {
      clearVideoStartTimer();

      const slide = slides[currentIndex];
      if (!slide) return;

      // enforce mute immediately (helps autoplay policies)
      enforceMuteOnSlide(slide);

      if (videoStartDelay <= 0) {
        playSlideMedia(slide);
        schedulePrewarm();
        return;
      }

      // schedule play after delay; only play if still active and not dragging
      videoStartTimer = setTimeout(() => {
        if (slides[currentIndex] === slide && !isDragging && !document.hidden) {
          playSlideMedia(slide);
          schedulePrewarm();
        }
      }, videoStartDelay * 1000);
    };

    // Hold the next slide's metadata fetch until the browser is idle, so it
    // never competes with the visible slide or with LCP.
    const schedulePrewarm = () => {
      const run = () => prewarmNextSlide(currentIndex);
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: 3000 });
      } else {
        setTimeout(run, 2000);
      }
    };

    // ---- Slider helpers ----
    const clampIndex = (i) => {
      if (i < 0) return slides.length - 1;
      if (i >= slides.length) return 0;
      return i;
    };

    const setTranslate = (px) => {
      track.style.transform = `translate3d(${-px}px, 0, 0)`;
    };

    const updateSizes = () => {
      slideWidth = viewport.clientWidth || root.clientWidth || 0;
      if (!slideWidth) return;

      slides.forEach((s) => {
        s.style.flex = "0 0 100%";
        s.style.width = `${slideWidth}px`;
      });

      setTranslate(currentIndex * slideWidth);
    };

    const clearOverlayTimer = () => {
      if (overlayTimer) {
        clearTimeout(overlayTimer);
        overlayTimer = null;
      }
    };

    const showOverlayTemporarily = (slide) => {
      if (!showOverlay) return;

      clearOverlayTimer();

      const overlay = slide ? slide.querySelector(".ewebjelly-vs-overlay") : null;
      if (!overlay) return;

      overlay.classList.add("is-visible");
      overlay.setAttribute("aria-hidden", "false");

      overlayTimer = setTimeout(() => {
        overlay.classList.remove("is-visible");
        overlay.setAttribute("aria-hidden", "true");
      }, overlaySeconds * 1000);
    };

    const buildDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = "";

      slides.forEach((_, idx) => {
        const b = document.createElement("button");
        b.type = "button";
        // The class was missing here, so every .ewebjelly-vs-dot rule in the
        // stylesheet — size, colour, the aria-current state — had never once
        // applied and the dots rendered as default empty buttons.
        b.className = "ewebjelly-vs-dot";
        b.setAttribute("data-dot", String(idx));
        b.setAttribute("aria-label", `Go to slide ${idx + 1}`);
        b.addEventListener("click", () => {
          goTo(idx);
          refreshAutoplay();
        });
        dotsWrap.appendChild(b);
      });
    };

    const updateActiveState = () => {
      slides.forEach((s, idx) => {
        const active = idx === currentIndex;
        s.classList.toggle("is-active", active);
        s.setAttribute("aria-hidden", active ? "false" : "true");
      });

      if (dotsWrap) {
        const dots = dotsWrap.querySelectorAll("button[data-dot]");
        dots.forEach((d) => {
          const idx = Number(d.getAttribute("data-dot"));
          d.setAttribute("aria-current", idx === currentIndex ? "true" : "false");
          d.classList.toggle("is-active", idx === currentIndex);
        });
      }

      // Pause everything immediately
      slides.forEach(pauseSlideMedia);

      // Play active slide (immediately or after delay)
      playActiveSlideWithDelay();

      // Overlay
      showOverlayTemporarily(slides[currentIndex]);
    };

    const goTo = (index, opts = {}) => {
      const { immediate = false } = opts;

      currentIndex = clampIndex(index);

      if (prefersReducedMotion || immediate) {
        track.style.transition = "none";
      } else {
        track.style.transition = ""; // CSS default
      }

      if (slideWidth) setTranslate(currentIndex * slideWidth);

      if (prefersReducedMotion || immediate) {
        // reflow then restore transitions
        // eslint-disable-next-line no-unused-expressions
        track.offsetHeight;
        track.style.transition = "";
      }

      updateActiveState();
    };

    const next = () => goTo(currentIndex + 1);
    const prev = () => goTo(currentIndex - 1);

    // ---- Autoplay ----
    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const canAutoplay = () => {
      return (
        autoplaySeconds > 0 &&
        !prefersReducedMotion &&
        !isHovering &&
        !isFocused &&
        !isDragging &&
        !document.hidden
      );
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!canAutoplay()) return;

      autoplayTimer = setInterval(() => {
        if (!canAutoplay()) return;
        next();
      }, autoplaySeconds * 1000);
    };

    const refreshAutoplay = () => {
      if (autoplaySeconds <= 0) return;
      startAutoplay();
    };

    // Only honour hover-to-pause on devices that actually hover. Touch browsers
    // synthesise mouseenter on tap and do not reliably send the matching
    // mouseleave, which left isHovering stuck true and killed rotation on
    // phones after the first tap.
    const canHover =
      !window.matchMedia || window.matchMedia("(hover: hover)").matches;

    root.addEventListener("mouseenter", () => {
      if (!canHover) return;
      isHovering = true;
      stopAutoplay();
      clearVideoStartTimer();
    });

    root.addEventListener("mouseleave", () => {
      if (!canHover) return;
      isHovering = false;
      refreshAutoplay();
      if (!document.hidden) playActiveSlideWithDelay();
    });

    root.addEventListener("focusin", (e) => {
      // Only a KEYBOARD focus should hold the carousel still.
      //
      // This used to latch on any focusin. Clicking an arrow or a dot focuses
      // that button, so isFocused went true and stayed true — the click
      // handler's own refreshAutoplay() then hit canAutoplay(), saw isFocused,
      // and returned without starting a timer. Focus stayed on the button, so
      // rotation was dead until the visitor clicked somewhere else on the page.
      // One tap of the next arrow turned auto-rotate off for the session.
      let keyboard = false;
      try {
        keyboard = !!(e.target && e.target.matches && e.target.matches(":focus-visible"));
      } catch (_) {}

      if (!keyboard) return;

      isFocused = true;
      stopAutoplay();
      clearVideoStartTimer();
    });

    root.addEventListener("focusout", () => {
      if (!isFocused) return;
      isFocused = false;
      refreshAutoplay();
      if (!document.hidden) playActiveSlideWithDelay();
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        stopAutoplay();
        clearVideoStartTimer();
      } else {
        refreshAutoplay();
        playActiveSlideWithDelay();
      }
    });

    // ---- Swipe/drag ----
    let startX = 0;
    let lastX = 0;
    let baseTranslate = 0;
    let pointerDownLink = null;

    const onPointerDown = (e) => {
      if (!slideWidth) return;

      // ✅ NEW: if clicking a link/button, don't start dragging
      if (isInteractiveTarget(e)) return;

      // Remember whether the gesture began on the whole-slide link.
      //
      // The native click CANNOT be relied on here. setPointerCapture below
      // retargets the compatibility mouse events — including `click` — at the
      // capture element, so the anchor never receives one and the browser
      // never navigates. The overlay button doesn't hit this because
      // isInteractiveTarget bails out before capture is ever set; the media
      // link is deliberately exempt from that check so swipe keeps working,
      // which is exactly what puts it in capture's path.
      //
      // So navigation is performed explicitly in onPointerUp.
      pointerDownLink =
        e.target && e.target.closest
          ? e.target.closest(".ewebjelly-vs-media-link")
          : null;

      isDragging = true;
      movedDuringDrag = false; // ✅ NEW
      stopAutoplay();
      clearVideoStartTimer();

      track.style.transition = "none";

      startX = e.clientX;
      lastX = startX;
      baseTranslate = currentIndex * slideWidth;

      try {
        viewport.setPointerCapture(e.pointerId);
      } catch (_) {}
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;

      lastX = e.clientX;
      const delta = lastX - startX;

      // ✅ NEW: flag true only after slight movement
      if (Math.abs(delta) > 6) movedDuringDrag = true;

      const translate = baseTranslate - delta;
      setTranslate(translate);
    };

    const onPointerUp = (e) => {
      if (!isDragging) return;

      isDragging = false;

      const delta = (lastX - startX) || 0;
      const threshold = Math.max(40, slideWidth * 0.15);
      const wasTap = !movedDuringDrag;

      track.style.transition = "";

      if (!wasTap) {
        if (Math.abs(delta) > threshold) {
          if (delta < 0) next();
          else prev();
        } else {
          // Dragged, but not past the threshold — snap back to where we were.
          goTo(currentIndex, { immediate: false });
        }
      }

      // A clean tap moves the track by nothing, so there is nothing to snap
      // back to. This used to call goTo() unconditionally, which ran
      // updateActiveState -> pauseSlideMedia -> playActiveSlideWithDelay — so
      // every tap on the video paused it and then restarted it
      // video_start_delay seconds later. That was the "tapping just pauses it"
      // behaviour, and it fired whether or not the slide had a link.

      refreshAutoplay();

      try {
        viewport.releasePointerCapture(e.pointerId);
      } catch (_) {}

      const link = pointerDownLink;
      pointerDownLink = null;

      // pointercancel means the gesture was taken away from us (scroll, system
      // gesture) — never navigate on that.
      if (!wasTap || !link || e.type === "pointercancel") return;
      if (e.button !== undefined && e.button !== 0) return;

      const href = link.getAttribute("href");
      if (!href) return;

      if (e.metaKey || e.ctrlKey) {
        window.open(href, "_blank", "noopener");
      } else {
        window.location.href = href;
      }
    };

    viewport.style.touchAction = "pan-y";
    viewport.addEventListener("pointerdown", onPointerDown);
    viewport.addEventListener("pointermove", onPointerMove);
    viewport.addEventListener("pointerup", onPointerUp);
    viewport.addEventListener("pointercancel", onPointerUp);

    // ✅ NEW: cancel click only if user dragged (prevents accidental link clicks on swipe)
    viewport.addEventListener(
      "click",
      (e) => {
        if (movedDuringDrag) {
          e.preventDefault();
          e.stopPropagation();
          movedDuringDrag = false;
        }
      },
      true
    );

    // ---- Arrows ----
    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        prev();
        refreshAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        next();
        refreshAutoplay();
      });
    }

    // Keyboard accessibility
    root.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
        refreshAutoplay();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
        refreshAutoplay();
      }
    });

    // ---- Init ----
    buildDots();
    updateSizes();
    goTo(0, { immediate: true });

    // Resize handling
    if ("ResizeObserver" in window) {
      const ro = new ResizeObserver(() => {
        updateSizes();
        goTo(currentIndex, { immediate: true });
      });
      ro.observe(viewport);
    } else {
      window.addEventListener("resize", () => {
        updateSizes();
        goTo(currentIndex, { immediate: true });
      });
    }

    // Start autoplay if enabled
    refreshAutoplay();
  });
})();